import { apiClient } from "@/http";
import type { JsonObject } from "./common";
import { apiRoutes } from "./apiRoutes";

// 연구자 노트. registry 가 training-infra `/annotations`(정책/eval-run/세션에 다는 note+tags)를
// 프록시한다(스펙 evaluation-notes). registry 는 노트를 저장하지 않는다. write/delete 는
// member+·entity scope gate 를 통과한 뒤 forward 한다. read 는 auth-only다. SoT 는 training-infra 다.

// 노트를 달 수 있는 training entity 종류(BE NoteEntityType 와 1:1).
export type NoteEntityType = "session" | "policy" | "eval_run";

// 노트 본문 길이 상한(BE NOTE_MAX_LENGTH 와 같다). UI maxLength 로도 쓴다.
export const NOTE_MAX_LENGTH = 4096;

// bulk read csv 상한(BE 계약). 넘으면 422 `training.note_bulk_too_large` 다. 호출부가 나눠 보낸다.
export const NOTE_BULK_MAX = 200;

// 단일 노트 view. bulk item·단건 조회·PUT 응답이 함께 쓴다. 단건 조회에서 노트가 없으면
// 빈 stub(note=null, tags={}, 시각 null)이 온다.
export type NoteView = {
  entity_type: NoteEntityType;
  entity_id: string;
  note: string | null;
  // 연구자 자유 서술 tags(예: `{"why_failed":"OOM"}`). 자유형 JSONB 라 구조를 좁히지 않는다.
  tags: JsonObject;
  author_id: string | null;
  created_at: string | null;
  updated_at: string | null;
};

// 다른 리소스가 목록에 포함하는 노트 참조. NoteView 의 축약형이다. 노트가 있는 entity 만
// (note 는 비어 있지 않음) 목록 셀에 미리보기로 표시한다. backend NoteRefResp 미러다.
export type NoteRef = {
  note: string;
  updated_at: string | null;
};

// UPSERT whole-resource payload. note·tags를 항상 함께 보낸다. 작성자 귀속은 응답에만 있다.
export type PutNoteReq = {
  note: string | null;
  tags: JsonObject;
};

// bulk 조회(auth-only). 노트가 있는 entity 만 돌아온다. entity_ids 는 csv 로 직렬화한다(≤200).
export async function listNotes(
  entityType: NoteEntityType,
  entityIds: readonly string[],
): Promise<NoteView[]> {
  const response = await apiClient.get<{ items: NoteView[] }>(apiRoutes.training.notes, {
    params: { entity_type: entityType, entity_ids: entityIds.join(",") },
  });
  return response.data.items;
}

// 단건 조회(auth-only). 노트가 없으면 빈 stub(note=null)과 200 이 온다.
export async function getNote(entityType: NoteEntityType, entityUuid: string): Promise<NoteView> {
  const response = await apiClient.get<NoteView>(apiRoutes.training.note(entityType, entityUuid));
  return response.data;
}

// UPSERT(member+·entity scope gate). 스코프 밖이거나 없는 entity 는 404 다.
export async function putNote(
  entityType: NoteEntityType,
  entityUuid: string,
  payload: PutNoteReq,
): Promise<NoteView> {
  const response = await apiClient.put<NoteView>(
    apiRoutes.training.note(entityType, entityUuid),
    payload,
  );
  return response.data;
}

// 삭제(member+·entity scope gate, 멱등). 204 가 온다.
export async function deleteNote(entityType: NoteEntityType, entityUuid: string): Promise<void> {
  await apiClient.delete(apiRoutes.training.note(entityType, entityUuid));
}

// 본문 정규화. 공백뿐인 본문은 "없음"이다.
//
// 상류 `has_note` 필터는 `note IS NOT NULL` 이다. 그래서 상류는 빈 문자열을 "노트 있음"으로 본다.
// registry 의 목록 미리보기는 빈 문자열을 버린다. 두 판정이 다르므로 빈 문자열을 wire 로 보내지
// 않는다.
function normalizedNoteBody(note: string): string | null {
  const trimmed = note.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * 노트 저장·비움의 wire 연산을 정하는 단일 지점.
 *
 * 모든 표면에서 "본문을 비웠다"를 이 함수 하나로 처리한다. 규칙은 하나다.
 * **기록 전체가 비면 레코드를 지운다. 태그가 남아 있으면 본문만 비운다.**
 *
 * 두 표면의 데이터 모델이 달라서 이 규칙이 필요하다:
 * - eval-run 편집기에서 태그는 1급 값이다. 본문 없이 태그만 있는 상태가 정상이다.
 *   이때 DELETE 를 보내면 태그가 사라진다.
 * - 세션 노트 패널에는 태그 편집 UI 가 없다. 그래서 태그는 대개 비어 있다.
 * 이 규칙 하나로 두 경우 모두 맞는 연산을 받는다.
 *
 * 세션 + archive 태그 잡에서 이 함수는 `note: null` PUT 을 보낸다. DELETE 를 보내면 상류는
 * soft-delete 마커를 지키려고 강등한다. 두 연산의 결과가 같으므로 안전하다.
 *
 * 반환값은 저장 후의 노트 상태다. 삭제한 경우 서버에서 노트가 없을 때 조회한 결과와 같은 모양의
 * 빈 stub 을 만들어 돌려준다. 그래서 소비처는 응답 유무로 분기하지 않는다.
 */
export async function saveNote(
  entityType: NoteEntityType,
  entityUuid: string,
  draft: { note: string; tags: JsonObject },
): Promise<NoteView> {
  const note = normalizedNoteBody(draft.note);
  const hasTags = Object.keys(draft.tags).length > 0;
  if (note === null && !hasTags) {
    await deleteNote(entityType, entityUuid);
    return emptyNoteView(entityType, entityUuid);
  }
  return putNote(entityType, entityUuid, { note, tags: draft.tags });
}

// 노트가 없을 때의 단건 조회 응답과 같은 모양. 삭제 직후 로컬 상태를 갱신할 때 쓴다.
export function emptyNoteView(entityType: NoteEntityType, entityUuid: string): NoteView {
  return {
    entity_type: entityType,
    entity_id: entityUuid,
    note: null,
    tags: {},
    author_id: null,
    created_at: null,
    updated_at: null,
  };
}
